import React, { useState, useEffect, useCallback, useRef } from 'react';
import { $InputLabel, $Input, $FormHelperText } from './styles';
import { $Italic } from '../newTypography';
import { SvgIcon, useDebounce, newTheme as theme } from '../';
import { $FormElementControl } from '../newFormElement/styles';
import { NumberInput } from './NumberInput';
export const Input = ({ title, hideLabel = false, hideItalic = false, description = null, boldDescription, name = 'input', error, errorMsg, debounce, allowFormat, decimal, ariaLabel = '', onChange = () => null, value = null, whiteBackground, endAdornment, startAdornment, withAutocomplete, actionIcon, ...rest }) => {
    const { type, required, multiline } = rest;
    const didMount = useRef(false);
    const [showPass, setShowPass] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const debouncedValue = useDebounce(inputValue, 300);
    const lastSentValueRef = useRef(value);
    const pendingCommitRef = useRef(false);
    const pendingCommitTimeoutRef = useRef();
    const droppedExternalRef = useRef({ has: false, value: null });
    const inputValueRef = useRef(value);
    useEffect(() => { inputValueRef.current = inputValue; });
    const applyDroppedExternal = () => {
        if (!droppedExternalRef.current.has)
            return;
        if (inputValueRef.current !== lastSentValueRef.current) {
            droppedExternalRef.current = { has: false, value: null };
            return;
        }
        const dropped = droppedExternalRef.current.value;
        droppedExternalRef.current = { has: false, value: null };
        setInputValue(dropped);
        lastSentValueRef.current = dropped;
    };
    const isTel = type === 'tel';
    const isNumber = type === 'number' || isTel;
    const isPassword = type === 'password';
    useEffect(() => {
        if (didMount.current && debounce && (value !== debouncedValue)) {
            const newValue = debouncedValue !== null ? debouncedValue : (isNumber ? null : '');
            lastSentValueRef.current = newValue;
            pendingCommitRef.current = true;
            droppedExternalRef.current = { has: false, value: null };
            clearTimeout(pendingCommitTimeoutRef.current);
            pendingCommitTimeoutRef.current = setTimeout(() => {
                pendingCommitRef.current = false;
                applyDroppedExternal();
            }, 3000);
            onChange(name, newValue);
        }
        else
            didMount.current = true;
    }, [debouncedValue]);
    useEffect(() => {
        if (!didMount.current || !debounce)
            return;
        if (inputValue !== debouncedValue)
            return;
        if (value === lastSentValueRef.current) {
            pendingCommitRef.current = false;
            clearTimeout(pendingCommitTimeoutRef.current);
            applyDroppedExternal();
            return;
        }
        if (value === inputValue)
            return;
        if (pendingCommitRef.current) {
            droppedExternalRef.current = { has: true, value: value };
            return;
        }
        setInputValue(value);
        lastSentValueRef.current = value;
    }, [value]);
    useEffect(() => () => clearTimeout(pendingCommitTimeoutRef.current), []);
    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        const cleanValue = (value && isNumber && value.slice(-1) === '.') ? value.slice(0, -1) : value;
        if (debounce)
            setInputValue(cleanValue);
        else
            onChange(name, cleanValue);
    }, [isNumber, debounce, name, onChange]);
    const displayValue = (debounce && didMount.current) ? inputValue : value;
    const Component = isNumber ? NumberInput : $Input;
    return (React.createElement($FormElementControl, null,
        !hideLabel &&
            React.createElement($InputLabel, { shrink: true },
                title,
                required && '*',
                actionIcon,
                !hideItalic &&
                    React.createElement($Italic, null, multiline ? 'textarea' : (type || 'text'))),
        React.createElement(Component, { ...rest, type: isPassword && showPass ? 'text' : type, key: name, value: displayValue, name: name, onChange: handleInputChange, hideLabel: hideLabel, error: error, isTel: isTel, allowFormat: allowFormat, decimal: decimal, whiteBackground: whiteBackground, autoComplete: withAutocomplete ? undefined : 'new-password', inputProps: { 'aria-label': ariaLabel, 'aria-autocomplete': withAutocomplete ? undefined : 'none', ...rest.inputProps }, startAdornment: startAdornment, endAdornment: endAdornment || (isPassword ? (showPass ? React.createElement(SvgIcon, { clickable: true, noPadding: true, color: theme.colors.icons.visibility, name: 'change_visibility', onClick: () => setShowPass(false), size: 20 }) :
                React.createElement(SvgIcon, { clickable: true, noPadding: true, color: theme.colors.icons.visibility, name: 'visibility-off', onClick: () => setShowPass(true), size: 20 })) : undefined) }),
        (description || errorMsg) &&
            React.createElement($FormHelperText, { error: error, boldDescription: boldDescription }, errorMsg || description)));
};
